import React from "react";
import { Link } from "react-router-dom";
import { Building2, Rocket, Target, Heart, Globe, Languages, Users, Sparkles, Mail, Phone, MapPin } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import InstallAppButton from "@/components/InstallAppButton";
import { SetuMark } from "@/components/Logo";

export default function About() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="about-logo">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-heading text-lg tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-2">
            <InstallAppButton />
            <LanguageSwitcher /><ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        {/* Hero — Global */}
        <section>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
            <Globe className="w-3 h-3" /> About Us · Global
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.1] sm:leading-tight max-w-4xl">
            The operating system for construction.<br />
            <span className="text-muted-foreground">Made in Bharat. Built for the world.</span>
          </h1>
          <p className="text-muted-foreground mt-4 sm:mt-6 text-sm sm:text-base max-w-3xl leading-relaxed">
            <strong className="text-foreground">SetuBuild Technologies</strong> is a global construction-technology company building <strong className="text-foreground">Setubuild.OS</strong> — an AI-first Site OS used by contractors, builders, and engineers across continents. Engineered from India for every language and every jobsite, from Mumbai to Manhattan, Lagos to London, São Paulo to Singapore. Available at <a href="https://setubuildos.com" className="text-primary hover:underline">setubuildos.com</a>.
          </p>

          {/* Global stats strip */}
          <div className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Stat n="17+" label="Languages Supported" />
            <Stat n="6" label="Continents Targeted" />
            <Stat n="24×7" label="Global Uptime" />
            <Stat n="INR / USD / EUR" label="Multi-Currency Ready" small />
          </div>
        </section>

        {/* Mission */}
        <section className="mt-14 sm:mt-20 border-t border-border pt-12 sm:pt-16">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Our Mission</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl">
            Every construction site on earth deserves precision software — in its own language, on any device, at any scale.
          </h2>
          <p className="text-muted-foreground mt-4 sm:mt-6 text-sm sm:text-base max-w-3xl leading-relaxed">
            Construction is a $13-trillion global industry, yet 80% of sites still run on WhatsApp forwards, spreadsheets, and memory. Setubuild.OS changes that — with AI photo inspection, voice-first daily reports, biometric attendance, and multi-language site operations built for a global workforce.
          </p>
        </section>

        {/* Values */}
        <section className="mt-14 sm:mt-20 border-t border-border pt-12 sm:pt-16">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">What we stand for</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-2xl">Principles that travel across borders.</h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10">
            <Value icon={<Globe className="w-4 h-4" />} title="Globally Local" desc="Global engineering, local intelligence. Every workflow adapts to the country you're in — currency, language, compliance, cultural norms of a jobsite." />
            <Value icon={<Languages className="w-4 h-4" />} title="Language-First" desc="17 languages at launch, scaling to 40+. Whether your foreman speaks Hindi, Spanish, Arabic or Swahili — Setubuild.OS speaks their language, natively." />
            <Value icon={<Sparkles className="w-4 h-4" />} title="AI, But Practical" desc="AI should save hours, not chase hype. Our photo inspector, DPRs, and civil chat are trained for the questions site engineers actually ask." />
            <Value icon={<Heart className="w-4 h-4" />} title="Data With Dignity" desc="DPDPA compliant (India). Passwords hashed with bcrypt, sessions over HTTPS, on-demand data export & account deletion. Your project data is yours — never sold." />
            <Value icon={<Users className="w-4 h-4" />} title="Worker-First" desc="Technology should meet workers where they are — on their phone, in their language, with a UI that respects their literacy level and network conditions." />
            <Value icon={<Rocket className="w-4 h-4" />} title="Bharat to Beyond" desc="India is where we prove the hard problems — languages, low bandwidth, offline-first. What works here, works everywhere." />
          </div>
        </section>

        {/* Where we operate */}
        <section className="mt-14 sm:mt-20 border-t border-border pt-12 sm:pt-16">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Global Reach</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl">Wherever you build, Setubuild.OS builds with you.</h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-8 sm:mt-10">
            <Region flag="🇮🇳" region="India · South Asia" note="Launch market · DPDPA compliant" primary />
            <Region flag="🌏" region="South-East Asia" note="Singapore, Vietnam, Indonesia" />
            <Region flag="🌍" region="Middle East & Africa" note="UAE, Saudi, Nigeria, Kenya" />
            <Region flag="🇬🇧" region="United Kingdom & Europe" note="Multi-currency support" />
            <Region flag="🇺🇸" region="North America" note="US & Canada · English + Spanish" />
            <Region flag="🌎" region="Latin America" note="Brazil, Mexico · Spanish & Portuguese" />
          </div>
          <p className="text-xs text-muted-foreground mt-4">Setubuild.OS is available worldwide via web. Localized enterprise support is being rolled out region by region.</p>
        </section>

        {/* Company Info */}
        <section className="mt-14 sm:mt-20 border-t border-border pt-12 sm:pt-16">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Company</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">SetuBuild Technologies</h2>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8">
            <InfoRow icon={<Building2 className="w-4 h-4" />} label="Legal Name" value="SetuBuild Technologies" />
            <InfoRow icon={<Rocket className="w-4 h-4" />} label="Product" value="Setubuild.OS — Global Construction Site OS" />
            <InfoRow icon={<Mail className="w-4 h-4" />} label="Business Email" value="setubuildtechnologies@gmail.com" href="mailto:setubuildtechnologies@gmail.com" />
            <InfoRow icon={<Phone className="w-4 h-4" />} label="Business Phone" value="+91 97667 44746" href="tel:+919766744746" />
            <InfoRow icon={<MapPin className="w-4 h-4" />} label="Global HQ" value="Maharashtra, India · Serving worldwide" />
            <InfoRow icon={<Target className="w-4 h-4" />} label="Founded" value="2026 · Bharat" />
            <InfoRow icon={<Globe className="w-4 h-4" />} label="Domain" value="setubuildos.com" href="https://setubuildos.com" />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-medium hover:opacity-90 transition" data-testid="about-contact-cta">
              Contact us <span aria-hidden>→</span>
            </Link>
            <Link to="/features" className="inline-flex items-center gap-2 bg-secondary border border-border rounded-md px-5 py-2.5 text-sm font-medium hover:border-primary/40 transition" data-testid="about-features-cta">
              Explore Setubuild.OS
            </Link>
          </div>
        </section>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">© {new Date().getFullYear()} SetuBuild Technologies. All rights reserved. · Made in Bharat · Built for the World</div>
          <div className="flex gap-4 mono text-[10px] uppercase tracking-widest">
            <Link to="/faq" className="hover:text-primary">FAQ</Link>
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/refund" className="hover:text-primary">Refund</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const Stat = ({ n, label, small }) => (
  <div className="border border-border rounded-lg p-4">
    <div className={`font-display ${small ? "text-xl" : "text-3xl"} leading-none`}>{n}</div>
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{label}</div>
  </div>
);

const Value = ({ icon, title, desc }) => (
  <div className="border border-border rounded-lg p-6 hover:border-primary/40 transition">
    <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center">{icon}</div>
    <h3 className="font-heading text-lg mt-4">{title}</h3>
    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
  </div>
);

const Region = ({ flag, region, note, primary }) => (
  <div className={`border rounded-lg p-4 transition ${primary ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/40"}`}>
    <div className="text-2xl leading-none">{flag}</div>
    <div className="font-heading text-sm mt-3">{region}</div>
    <div className="text-xs text-muted-foreground mt-1">{note}</div>
  </div>
);

const InfoRow = ({ icon, label, value, href }) => {
  const body = (
    <>
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="w-7 h-7 rounded-md bg-secondary grid place-items-center">{icon}</span>
        <span className="mono text-[10px] uppercase tracking-[0.2em]">{label}</span>
      </div>
      <div className="mt-2 text-sm font-medium">{value}</div>
    </>
  );
  const cls = "block border border-border rounded-lg p-4 hover:border-primary/40 transition";
  return href ? <a href={href} className={cls}>{body}</a> : <div className={cls}>{body}</div>;
};
